import { Injectable, isDevMode } from '@angular/core';
import { HttpTransportType, HubConnectionBuilder, IHttpConnectionOptions, LogLevel } from '@microsoft/signalr';
import { ContactWebSocketService } from 'contact-dash';
import { Subject } from 'rxjs';
import { StarbaseLoginService } from 'starbase-login';
import { UserConnectionlogModel, UserType } from '../models/log-ext';
import { UserDataService } from './user-data.service';
import { AppGlobalsService } from '../app-globals.service';
import { UserAccountModel, UserSubscriptionModel } from 'starbase-launcher';
import { UserSubscriptionModel_ext } from '../models/userSubscription';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class WebSocketService extends ContactWebSocketService {
  protected endpoint = '/hub';

  private _imageUpdatedTrigger$: Subject<[number, string]>;
  private _qmUpdatedTrigger$: Subject<[number, string]>;
  private imageUpdated = 'updatedImage';
  private qmUpdated = 'updatedQM';

  constructor(private _loginSvc: StarbaseLoginService, private appGlobalsSvc: AppGlobalsService) {
    super(_loginSvc);
    if (this._loginSvc.isLoggedIn()) {
      this.initWebSocket();
      this.connection.onclose(() => {
        this.disconnectUserConnection();
      });
    } else {
      this._loginSvc.loginState.subscribe((loggedIn) => {
        if (loggedIn) {
          this.initWebSocket();
          this.connection.onclose(() => {
            this.disconnectUserConnection();
          });
        }
      });
    }
    this._contactUpdatedTrigger$ = new Subject<number>();
    this._imageUpdatedTrigger$ = new Subject<[number, string]>();
    this._qmUpdatedTrigger$ = new Subject<[number, string]>();
  }

  public get imageUpdatedTrigger$(): Subject<[number, string]> {
    return this._imageUpdatedTrigger$;
  }
  public get qmUpdatedTrigger$(): Subject<[number, string]> {
    return this._qmUpdatedTrigger$;
  }

  public setupImageConnection(): void {
    this.manageConnection('RegisterImageConnection');
  }

  public disconnectImageConnection(): void {
    this.manageConnection('UnregisterImageConnection');
  }

  public setupQMConnection(): void {
    this.manageConnection('RegisterQMConnection');
  }

  public disconnectQMConnection(): void {
    this.manageConnection('UnregisterQMConnection');
  }

  public setupUserConnection(): void {
    console.log('Setting up User Web socket connection.');
    this.appGlobalsSvc.currentLoggedInUser.subscribe(u => {
      const token = this._loginSvc.getUserModel();
      const user = new UserConnectionlogModel();
      user.userId = u.uid;
      user.userName = token.user;
      user.isSubscriber = !token.isAdmin() && token.isInUserGroup('Subscriber');
      if (token.isAdmin()) {
        user.userType = UserType.Admin;
      } else if (token.isInUserGroup('Librarian'))  {
        user.userType = UserType.Librarian;
      } else if (token.isInUserGroup('Subscriber')) {
        const sub = (u as UserAccountModel).subscriptionRecord as UserSubscriptionModel_ext;
        if (sub && sub.active) {
          switch (sub.subscriptionPriceUID) {
            case (isDevMode() || environment.testMode ? environment.subscriptions.Student : 1):
              user.userType = UserType.Student;
              break;
            case (isDevMode() || environment.testMode ? environment.subscriptions.Monthly : 2):
              user.userType = UserType.Monthly;
              break;
            case (isDevMode() || environment.testMode ? environment.subscriptions.Annual : 4):
              user.userType = UserType.Annual;
              break;
            default:
              user.userType = UserType.Other;
              break;
          }
        } else {
          user.userType = UserType.Free;
        }
      } else if (token.isInUserGroup('Trial')) {
        user.userType = UserType.Trial;
      } else {
        user.userType = UserType.Other;
      }
      this.manageConnection('RegisterUserConnection', user);
    });
  }

  public disconnectUserConnection(): void {
    console.log('Disconnecting User Web socket connection.');
    this.manageConnection('UnregisterUserConnection');
  }

  protected configureConnection(): void {
    super.configureConnection();

    this.connection.on(this.contactUpdated, (uid) => {
      this.contactUpdatedTrigger$.next(+uid);
    });

    this.connection.on(this.imageUpdated, (uid: number, user: string) => {
      this.imageUpdatedTrigger$.next([uid, user]);
    });

    this.connection.on(this.qmUpdated, (uid: number, user: string) => {
      this.qmUpdatedTrigger$.next([uid, user]);
    });
  }

}
